import {Component, Input} from '@angular/core';
import {CompanyResponse, EventTypeResponse} from '../../../../../../@shared/models/response.module';
import {EventsService} from '../../../../../../@shared/services/events-service';
import {_AQH_EVE_1, _AQH_EVE_5, AQH_EVE_1, AQH_EVE_4, AQH_EVE_5} from '../../../../../../@shared/models/input.module';
import {ToastService} from '../../../../../../@core/services/toast.service';
import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';

@Component({
  selector: 'app-add-event',
  standalone: false,
  templateUrl: './add-event.html',
  styleUrl: './add-event.css'
})
export class AddEvent {
  @Input() company: CompanyResponse
  eventTypes: EventTypeResponse[]

  event_name: string = ''
  event_description: string = ''
  event_type_id: EventTypeResponse
  start_date: string | null;
  end_date: string | null;
  address: string;
  postal_code: string;
  city: string;
  country: string;
  registration_deadline: string;
  logo?: File;
  header?: File;

  submitted: boolean = false;
  saving: boolean = false;

  constructor(
    private eventService: EventsService,
    private toastService: ToastService,
    private translate: TranslateService,
    private router: Router
  ) {}

  get isEventNameInvalid(): boolean {
    return this.submitted && !this.event_name?.trim();
  }

  get isEventTypeInvalid(): boolean {
    return this.submitted && !this.event_type_id;
  }

  get isStartDateInvalid(): boolean {
    return this.submitted && !this.start_date;
  }

  get isFormValid(): boolean {
    return !!this.event_name?.trim() && !!this.event_type_id && !!this.start_date;
  }

  ngOnInit() {
    this.getEventTypes()
  }

  getEventTypes() {
    let params: AQH_EVE_4 = {}
    this.eventService.getEventTypes(params).subscribe((res) => {
      this.eventTypes = this.filterEventTypesByCompanyType(res || [])
    })
  }

  filterEventTypesByCompanyType(eventTypes: EventTypeResponse[]): EventTypeResponse[] {
    if (!this.company) return eventTypes;

    // Event type IDs: 0 = Competition, 1 = Social, 2 = Training
    if (this.company.company_type === 'stable') {
      // Stables cannot create competition events
      return eventTypes.filter(et => et.event_type_id !== 0);
    } else if (this.company.company_type === 'stores_services') {
      // Stores & Services can only create social events
      return eventTypes.filter(et => et.event_type_id === 1);
    }
    return eventTypes;
  }

  addEvent() {
    this.submitted = true;

    if (!this.isFormValid) {
      this.toastService.error(this.translate.instant('validation.fillRequiredFields'));
      return;
    }

    this.saving = true;

    let params: _AQH_EVE_1 = {
      event_name: this.event_name,
      event_description: this.event_description,
      event_type_id: this.event_type_id.event_type_id,
      start_date: this.start_date!,
      end_date: this.end_date || this.start_date!,
      registration_deadline: this.registration_deadline,
      address: this.address,
      postal_code: this.postal_code,
      city: this.city,
      country: this.country,
    }
    this.eventService.addEvent(params).subscribe({
      next: (res) => {
        const eventId = res.data.event_id;
        let pendingUploads = 0;

        if (this.logo) {
          pendingUploads++;
          let logoParams: _AQH_EVE_5 = {
            event_id: eventId,
            image_type: 'logo',
            image: this.logo
          }
          this.eventService.editEventFiles(logoParams).subscribe({
            next: () => {
              pendingUploads--;
              this.checkComplete(pendingUploads, eventId);
            },
            error: () => {
              pendingUploads--;
              this.checkComplete(pendingUploads, eventId);
            }
          });
        }

        if (this.header) {
          pendingUploads++;
          let headerParams: _AQH_EVE_5 = {
            event_id: eventId,
            image_type: 'header',
            image: this.header
          }
          this.eventService.editEventFiles(headerParams).subscribe({
            next: () => {
              pendingUploads--;
              this.checkComplete(pendingUploads, eventId);
            },
            error: () => {
              pendingUploads--;
              this.checkComplete(pendingUploads, eventId);
            }
          });
        }

        if (pendingUploads === 0) {
          this.onSuccess(eventId);
        }
      },
      error: (err) => {
        this.saving = false;
        this.toastService.error(err.error?.reason || this.translate.instant('companyProfile.addEvent.error'));
      }
    });
  }

  private checkComplete(pendingUploads: number, eventId: number) {
    if (pendingUploads === 0) {
      this.onSuccess(eventId);
    }
  }

  private onSuccess(eventId: number) {
    this.saving = false;
    this.toastService.success(this.translate.instant('companyProfile.addEvent.success'));
    this.router.navigate([`company-profile/manage-event/${eventId}`]);
  }
}
